import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { AddContactForm } from './AddContactForm';
import { EditableAddress, MapSquare } from './AddressSection';
import { findOwnLocationNode, findNodeById } from './HierarchyTree';
import {
  Field,
  OptionalField,
  labelSx,
  valueSx,
  formatAddress,
  getContactPhone,
  getContactName,
  findAttr,
  formatAttrAddress,
  formatAttrContact,
  extractReferences,
} from './LocationHelpers';
import { LocationPanelProps } from './LocationPanel.props';
import { StructureHierarchySection } from './StructureHierarchySection';
import {
  parseTradingHours,
  parseCoverageHours,
  CombinedHoursDisplay,
  HoursDisplay,
} from './TradingHoursSection';
import { ActionPermissions } from '../../../../../@types';
import { Panel } from '../../../../../components/atoms/Panel';
import { isUserHasPermission } from '../../../../../configs/permissions';
import { useIsClient } from '../../../../../hooks/useIsClient';

// ── Main Component ───────────────────────────────────────────

export const LocationPanel: React.FC<LocationPanelProps> = ({
  service,
  nodeAttributes,
  tradingHoursData,
  coverageHoursData,
  permissions,
  onSaveAddress,
  onSaveContact,
}) => {
  const hierarchy = service?.locationHierarchy ?? null;
  const mainNodeId = service?.mainLocationNodeId ?? 0;
  const attrs = nodeAttributes ?? [];

  // Extract outlet (physical location) and terminal/sublocation from hierarchy.
  // Anchored to mainNodeId's own path — not just "the shallowest outlet
  // anywhere in the tree" — so this is right even if the fetched hierarchy's
  // top node sits above the job's own location (e.g. a brand with other
  // outlets as siblings).
  const outletNode = findOwnLocationNode(hierarchy, mainNodeId);
  const mainNode = findNodeById(hierarchy, mainNodeId);
  const outletName = outletNode?.name ?? service?.mainLocation ?? '-';
  const sublocationName =
    mainNode && mainNode.id !== outletNode?.id
      ? mainNode.name
      : String(service?.subLocation ?? '-');

  // Address: prefer service-level override, fall back to node main address
  const serviceAddr = formatAddress(service?.serviceAddress);
  const mainAddrAttr = findAttr(attrs, 'address', 'Main');
  const nodeAddr = formatAttrAddress(mainAddrAttr);
  const address = serviceAddr !== '-' ? serviceAddr : nodeAddr;

  // Contact from node attributes
  const mainContact = formatAttrContact(findAttr(attrs, 'contact', 'Main'));
  const fallbackPhone = getContactPhone(service?.contactData);
  const fallbackName = getContactName(service?.contactData);
  const contactName = mainContact.name !== '-' ? mainContact.name : fallbackName;
  const phone = mainContact.phone !== '-' ? mainContact.phone : fallbackPhone;

  // Trading hours and SLA coverage from node attributes
  const tradingHours = parseTradingHours(tradingHoursData);
  const coverageHours = parseCoverageHours(coverageHoursData);

  // References from node attributes
  const locRefs = extractReferences(attrs);
  const svc = service as unknown as Record<string, unknown> | null;
  const subRefs = svc?.subLocationReferences as Record<string, string> | undefined;
  const subRef1 = subRefs?.referenceNo1;
  const subRef2 = subRefs?.referenceNo2;
  const subRef3 = subRefs?.referenceNo3;

  const canViewStructure = isUserHasPermission(
    ActionPermissions.Structure_Edit_View_Structure_Location,
    permissions
  );
  const canEditServiceParameters = isUserHasPermission(
    ActionPermissions.Service_Edit_Service_Parameters_General,
    permissions
  );
  // Client/customer users see a stripped-down Location panel: trading hours
  // only (no SLA Coverage column — internal-only) and no Structure Hierarchy
  // tree (exposes sibling locations they shouldn't see).
  const isClient = useIsClient();
  const outletId = outletNode?.id;
  const subId = service?.subLocationNodeId;

  return (
    <Panel title="Location">
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {/* Left: Details */}
        <Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={labelSx}>Location</Typography>
            {canViewStructure && outletId ? (
              <MuiLink
                component={RouterLink}
                to={`/structure/${outletId}`}
                underline="hover"
                sx={valueSx}
              >
                {outletName}
              </MuiLink>
            ) : (
              <Typography sx={valueSx}>{outletName || '—'}</Typography>
            )}
          </Box>
          <EditableAddress
            serviceAddress={service?.serviceAddress}
            displayValue={address}
            onSave={canEditServiceParameters ? onSaveAddress : undefined}
          />
          <Box sx={{ mb: 1 }}>
            <Typography sx={labelSx}>Sublocation</Typography>
            {canViewStructure && subId ? (
              <MuiLink
                component={RouterLink}
                to={`/structure/${subId}`}
                underline="hover"
                sx={valueSx}
              >
                {sublocationName}
              </MuiLink>
            ) : (
              <Typography sx={valueSx}>{sublocationName || '—'}</Typography>
            )}
          </Box>

          <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', my: 1 }} />

          <Field label="Contact Name" value={contactName} />
          <Field label="Contact Phone" value={phone} />
          {onSaveContact && canEditServiceParameters && (
            <AddContactForm onSaveContact={onSaveContact} />
          )}

          <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', my: 1 }} />

          <OptionalField label="Location Ref #1" value={locRefs.ref1} />
          <OptionalField label="Location Ref #2" value={locRefs.ref2} />
          <OptionalField label="Location Ref #3" value={locRefs.ref3} />
          <OptionalField label="Sublocation Ref #1" value={subRef1} />
          <OptionalField label="Sublocation Ref #2" value={subRef2} />
          <OptionalField label="Sublocation Ref #3" value={subRef3} />

          {isClient && tradingHours && (
            <Box sx={{ '@media print': { display: 'none' } }}>
              <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', my: 1 }} />
              <Box sx={{ mb: 1 }}>
                <HoursDisplay days={tradingHours} />
              </Box>
            </Box>
          )}
          {!isClient && (
            <Box sx={{ '@media print': { display: 'none' } }}>
              <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', my: 1 }} />
              <Box sx={{ mb: 1 }}>
                <CombinedHoursDisplay tradingHours={tradingHours} coverageHours={coverageHours} />
              </Box>
            </Box>
          )}
        </Box>

        {/* Right: Map + Structure Hierarchy */}
        <Box>
          <MapSquare address={address !== '-' ? address : outletName} />

          {!isClient && (
            <StructureHierarchySection
              hierarchy={outletNode}
              activeNodeId={subId}
              canViewStructure={canViewStructure}
            />
          )}
        </Box>
      </Box>
    </Panel>
  );
};
